/* Slash command list + tippy renderer (Phase 5e §2.4). */
import tippy from 'tippy.js';

function csrf() {
  return document.querySelector('meta[name="csrf-token"]')?.content
      || document.querySelector('input[name="csrf_token"]')?.value || '';
}

async function uploadImage(file, onProgress) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await fetch('/api/uploads/r2-image', {
    method: 'POST', body: fd,
    headers: { 'X-CSRFToken': csrf() },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  const data = await r.json();
  return data.url;
}

function pickImageFile() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = () => {
      const f = input.files && input.files[0];
      if (!f) return reject(new Error('No file picked'));
      resolve(f);
    };
    input.click();
  });
}

function promptForUrl(label) {
  // eslint-disable-next-line no-alert
  const v = window.prompt(label, 'https://');
  if (!v) return null;
  if (!/^https?:\/\//i.test(v)) {
    // eslint-disable-next-line no-alert
    window.alert('URL must start with http:// or https://');
    return null;
  }
  return v;
}

export const COMMANDS = [
  {
    title: 'Heading 2',
    description: 'Section heading',
    aliases: ['h2', 'heading'],
    icon: 'H2',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
  },
  {
    title: 'Heading 3',
    description: 'Subsection',
    aliases: ['h3', 'subheading'],
    icon: 'H3',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
  },
  {
    title: 'Bulleted list',
    description: 'Unordered list',
    aliases: ['bullet', 'ul', 'list'],
    icon: '•',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    title: 'Numbered list',
    description: 'Ordered list',
    aliases: ['number', 'ol', 'ordered'],
    icon: '1.',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    title: 'Quote',
    description: 'Highlighted callout',
    aliases: ['quote', 'callout', 'blockquote'],
    icon: '“”',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setBlockquote().run(),
  },
  {
    title: 'Code block',
    description: 'Monospaced snippet',
    aliases: ['code', 'snippet'],
    icon: '<>',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setCodeBlock().run(),
  },
  {
    title: 'Divider',
    description: 'Horizontal rule',
    aliases: ['divider', 'hr', 'separator'],
    icon: '—',
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
  {
    title: 'Image',
    description: 'Upload from disk',
    aliases: ['image', 'photo', 'picture'],
    icon: '🖼',
    command: async ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      try {
        const file = await pickImageFile();
        const url = await uploadImage(file);
        editor.chain().focus().setImage({ src: url }).run();
      } catch (e) {
        if (e.message !== 'No file picked') alert(e.message);
      }
    },
  },
  {
    title: 'Video embed',
    description: 'YouTube / Vimeo URL',
    aliases: ['video', 'youtube', 'vimeo'],
    icon: '▶',
    command: ({ editor, range }) => {
      const url = promptForUrl('Video URL (YouTube or Vimeo):');
      if (!url) return;
      // Tiptap doesn't ship a video node by default; render a responsive
      // iframe wrapper as raw HTML for v1.
      const yt = url.match(/youtu(?:\.be|be\.com)\/(?:watch\?v=)?([\w-]{11})/);
      const vm = url.match(/vimeo\.com\/(\d+)/);
      let embed = null;
      if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
      else if (vm) embed = `https://player.vimeo.com/video/${vm[1]}`;
      else embed = url;
      const html = `<div class="cs-video-embed" data-src="${embed}">`
        + `<iframe src="${embed}" frameborder="0" allowfullscreen></iframe></div>`;
      editor.chain().focus().deleteRange(range).insertContent(html).run();
    },
  },
  {
    title: 'Link bookmark',
    description: 'Inline link',
    aliases: ['bookmark', 'link', 'embed'],
    icon: '🔗',
    command: ({ editor, range }) => {
      const url = promptForUrl('Link URL:');
      if (!url) return;
      editor.chain().focus().deleteRange(range)
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url } }],
        })
        .run();
    },
  },
];

export function renderCommandList() {
  let popover = null;
  let listEl = null;
  let activeIndex = 0;
  let currentItems = [];
  let currentProps = null;

  function rebuild(items, props) {
    currentItems = items;
    currentProps = props;
    activeIndex = 0;
    if (!listEl) return;
    listEl.innerHTML = '';
    if (!items.length) {
      const e = document.createElement('div');
      e.className = 'cs-slash-empty';
      e.textContent = 'No matches';
      listEl.appendChild(e);
      return;
    }
    items.forEach((it, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cs-slash-item' + (idx === 0 ? ' cs-slash-active' : '');
      btn.innerHTML = `
        <span class="cs-slash-icon">${it.icon || '•'}</span>
        <span class="cs-slash-text">
          <span class="cs-slash-title">${it.title}</span>
          ${it.description ? `<span class="cs-slash-desc">${it.description}</span>` : ''}
        </span>`;
      btn.addEventListener('mouseenter', () => setActive(idx));
      btn.addEventListener('mousedown', (e) => { e.preventDefault(); pick(idx); });
      listEl.appendChild(btn);
    });
  }

  function setActive(idx) {
    if (!listEl) return;
    const btns = listEl.querySelectorAll('.cs-slash-item');
    if (!btns.length) return;
    activeIndex = (idx + btns.length) % btns.length;
    btns.forEach((b, i) => b.classList.toggle('cs-slash-active', i === activeIndex));
    btns[activeIndex].scrollIntoView({ block: 'nearest' });
  }

  function pick(idx) {
    if (!currentProps || !currentItems.length) return;
    const item = currentItems[idx];
    if (!item) return;
    currentProps.command(item);
  }

  return {
    onStart: (props) => {
      listEl = document.createElement('div');
      listEl.className = 'cs-slash-menu';
      rebuild(props.items, props);
      popover = tippy('body', {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: listEl,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        arrow: false,
        theme: 'cs-slash',
      })[0];
    },
    onUpdate: (props) => {
      if (!popover) return;
      rebuild(props.items, props);
      popover.setProps({ getReferenceClientRect: props.clientRect });
    },
    onKeyDown: (props) => {
      if (!popover) return false;
      if (props.event.key === 'ArrowDown') { setActive(activeIndex + 1); return true; }
      if (props.event.key === 'ArrowUp') { setActive(activeIndex - 1); return true; }
      if (props.event.key === 'Enter') { pick(activeIndex); return true; }
      if (props.event.key === 'Escape') { popover.hide(); return true; }
      return false;
    },
    onExit: () => {
      if (popover) { popover.destroy(); popover = null; }
      listEl = null;
    },
  };
}
